import React from 'react';
import { Card, Row, Col, Typography, Image } from 'antd';
import styled from 'styled-components';

const { Title, Text } = Typography;

interface WizardStepProps {
  stepNumber: number;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  reverse?: boolean;
}

const StepContainer = styled.div<{ $reverse: boolean }>`
  margin: 48px 0;
  
  @media (max-width: 768px) {
    margin: 32px 0;
  }
`;

const StepCard = styled(Card)`
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .ant-card-body {
    padding: 40px;
    
    @media (max-width: 768px) {
      padding: 24px;
    }
  }
`;

const StepNumber = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4096ff 0%, #1677ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin-bottom: 24px;
  box-shadow: 0 8px 16px rgba(64, 150, 255, 0.3);
`;

const StepTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 16px;
    font-size: 28px;
    font-weight: 700;
    color: #1a1a2e;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
`;

const StepDescription = styled(Text)`
  font-size: 16px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.6;
  display: block;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  
  .ant-image {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }
  
  img {
    border-radius: 16px;
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
    }
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

export const WizardStep: React.FC<WizardStepProps> = ({
  stepNumber,
  title,
  description,
  image,
  imageAlt,
  reverse = false
}) => {
  const imageCol = (
    <Col xs={24} md={12} order={reverse ? 2 : 1}>
      <ImageContainer>
        <Image
          src={image}
          alt={imageAlt}
          preview={false}
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </ImageContainer>
    </Col>
  );

  const contentCol = (
    <Col xs={24} md={12} order={reverse ? 1 : 2}>
      <ContentContainer>
        <StepNumber>{stepNumber}</StepNumber>
        <StepTitle level={2}>{title}</StepTitle>
        <StepDescription>{description}</StepDescription>
      </ContentContainer>
    </Col>
  );

  return (
    <StepContainer $reverse={reverse}>
      <StepCard bordered={false}>
        <Row align="middle" gutter={[48, 32]}>
          {imageCol}
          {contentCol}
        </Row>
      </StepCard>
    </StepContainer>
  );
};







